/** Минимальная типизация того, чем мы реально пользуемся из Telegram WebApp SDK. */
interface HapticFeedback {
  impactOccurred(style: 'light' | 'medium' | 'heavy' | 'rigid' | 'soft'): void;
  notificationOccurred(type: 'error' | 'success' | 'warning'): void;
  selectionChanged(): void;
}

interface BackButton {
  show(): void;
  hide(): void;
  onClick(cb: () => void): void;
  offClick(cb: () => void): void;
}

interface TelegramWebApp {
  initData: string;
  initDataUnsafe: { start_param?: string; user?: { id: number; first_name?: string } };
  colorScheme: 'light' | 'dark';
  themeParams: Record<string, string>;
  HapticFeedback?: HapticFeedback;
  BackButton: BackButton;
  ready(): void;
  expand(): void;
  disableVerticalSwipes?(): void;
  onEvent(event: string, cb: () => void): void;
  openTelegramLink(url: string): void;
  shareMessage?(id: string, cb?: (sent: boolean) => void): void;
  close(): void;
}

declare global {
  interface Window {
    Telegram?: { WebApp: TelegramWebApp };
  }
}

export const tg = (): TelegramWebApp | undefined => window.Telegram?.WebApp;

/** Тема Telegram может поменяться на лету — отражаем её на корне документа. */
function applyTheme(): void {
  const app = tg();
  document.documentElement.dataset.theme = app?.colorScheme ?? 'light';
}

export function initTelegram(): void {
  const app = tg();
  if (!app) {
    // Открыли в обычном браузере (например, при разработке).
    document.documentElement.dataset.theme = window.matchMedia('(prefers-color-scheme: dark)')
      .matches
      ? 'dark'
      : 'light';
    return;
  }
  app.ready();
  app.expand();
  // Свайп вниз внутри игры закрывал бы приложение прямо посреди примера.
  app.disableVerticalSwipes?.();
  applyTheme();
  app.onEvent('themeChanged', applyTheme);
}

/** Строка подписи, которую сервер проверяет на каждом запросе. */
export function initData(): string {
  return tg()?.initData ?? '';
}

export function startParam(): string | undefined {
  return tg()?.initDataUnsafe.start_param;
}

export const haptic = {
  tap(): void {
    tg()?.HapticFeedback?.selectionChanged();
  },
  right(): void {
    tg()?.HapticFeedback?.notificationOccurred('success');
  },
  wrong(): void {
    tg()?.HapticFeedback?.notificationOccurred('error');
  },
  win(): void {
    tg()?.HapticFeedback?.impactOccurred('heavy');
  },
};

/** Системная кнопка «назад» вместо своей — так привычнее внутри Telegram. */
export function showBackButton(handler: (() => void) | null): void {
  const app = tg();
  if (!app) return;
  if (!handler) {
    app.BackButton.hide();
    return;
  }
  app.BackButton.onClick(handler);
  app.BackButton.show();
}

export function hideBackButton(handler: () => void): void {
  const app = tg();
  if (!app) return;
  app.BackButton.offClick(handler);
  app.BackButton.hide();
}

export function shareLink(url: string, text: string): void {
  const app = tg();
  const share = `https://t.me/share/url?url=${encodeURIComponent(url)}&text=${encodeURIComponent(text)}`;
  if (app) app.openTelegramLink(share);
  else window.open(share, '_blank');
}
